import { useCallback } from 'react';
import {
  useLocation,
  useNavigate,
  useParams as useReactRouterParams,
  useSearchParams as useReactRouterSearchParams,
} from 'react-router-dom';

type NavigateOptions = { replace?: boolean; state?: unknown };

export interface AppRouter {
  push: (path: string, options?: NavigateOptions) => void;
  replace: (path: string, options?: NavigateOptions) => void;
  back: () => void;
  pathname: string;
}

export function useRouter(): AppRouter {
  const navigate = useNavigate();
  const location = useLocation();

  const push = useCallback<AppRouter['push']>(
    (path, options) => {
      navigate(path, { replace: options?.replace ?? false, state: options?.state });
    },
    [navigate]
  );

  const replace = useCallback<AppRouter['replace']>(
    (path, options) => {
      navigate(path, { replace: true, state: options?.state });
    },
    [navigate]
  );

  const back = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    push,
    replace,
    back,
    pathname: location.pathname,
  };
}

export function usePathname(): string {
  const location = useLocation();
  return location.pathname;
}

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return useReactRouterParams() as unknown as T;
}

export function useSearchParams() {
  return useReactRouterSearchParams();
}
